import bowser from 'bowser';

/**
 * 首字母大写
 * @param {String} string 处理
 */
const toFirstCharUpCase = string => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * 设置DOM属性
 * @param {dom} el 需要设置的DOM
 * @param {any} name 属性名
 * @param {any} val
 */
const setAttribute = (el, name, val) => el.setAttribute(name, val);

/**
 * 加载video
 *
 * @param {dom} videoEl 视频DOM
 */
const loadVideo = videoEl => {
    if (typeof videoEl.load === 'function') {
        // wrapping in an iife so it's not deoptimized (#1060#discussion_r10324473)
        (() => {
            try {
                videoEl.load();
            } catch (e) {
                // satisfy linter
            }
        })();
    }
};

/**
 * 重置video
 *
 * @param {dom} el 视频DOM
 * @return {undefined}
 */
const resetVideo = el => {
    if (!el) {
        return;
    }

    const sources = el.querySelectorAll('source');
    let i = sources.length;

    while (i--) {
        el.removeChild(sources[i]);
    }

    // remove any src reference.
    // not setting `src=''` because that throws an error
    el.removeAttribute('src');
    loadVideo(el);
};

/**
 * 合并对象
 * @param {Object} originObj 原对象
 * @param {Object} mergeObj 覆盖对象
 */
const mergeObj = (originObj, mergeObj) => {
    for (const mergeKey in mergeObj) {
        originObj[mergeKey] = mergeObj[mergeKey];
    }

    return originObj;
};

/**
 * 获取所有cookies
 */
const getAllCookies = () => {
    const cookieArr = [];

    if (document.cookie !== '') {
        const cStrArr = document.cookie.split(';');

        for (let i = 0, l = cStrArr.length; i < l; i++) {
            const cStr = cStrArr[i];
            const index = cStr.indexOf('=');
            const name = cStr.substring(0, index).replace(/[ ]/g, '');
            const value = cStr.substring(index + 1);
            const cookie = {};

            cookie.name = name;
            cookie.value = value;
            cookieArr.push(cookie);
        }
    }

    return cookieArr;
};

/**
 * 获取cookies
 * @param {String} name cookie名
 */
const playerGetCookie = name => {
    const cookieArr = getAllCookies();

    for (let i = 0, l = cookieArr.length; i < l; i++) {
        const cookie = cookieArr[i];

        if (cookie.name === name) {
            const doFn = decodeURIComponent;

            return doFn(cookie.value);
        }
    }

    return '';
};

/**
 * 设置cookie
 * @param {String} name
 * @param {any} value
 * @param {String} path
 * @param {String} domain
 * @param {String} expires
 * @param {String} secure
 */
const playerSetCookie = (name, value, path, domain, expires, secure) => {
    const doFn = encodeURIComponent;
    const cStrArr = [];

    cStrArr.push(`${name}=${doFn(value)}`);
    if (expires) {
        const date = new Date();

        date.setTime(date.getTime() + expires * 1000 * 60);
        cStrArr.push(`; expires=${date.toGMTString()}`);
    }
    cStrArr.push(path ? `; path=${path}` : '');
    cStrArr.push(domain ? `; domain=${domain}` : '');
    cStrArr.push(secure ? '; secure' : '');
    document.cookie = cStrArr.join('');
};

/**
 * 创建uid
 */
const createUid = () => {
    const key = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
    /**
     * 28位
     */
    let ramStr = parseInt(Math.random() * 268435455).toString(2);

    /**
     * 44位
     */
    let mtimeStr = new Date().getTime().toString(2); // eslint-disable-line

    while (ramStr.length < 28) ramStr = `0${ramStr}`;
    while (mtimeStr.length < 44) mtimeStr = `0${mtimeStr}`;
    const binaryStr = ramStr + mtimeStr;

    let outStr = '';

    for (let i = 0; i < 12; i++) {
        outStr += key.charAt(parseInt(binaryStr.substr(i * 6, 6), 2));
    }

    return outStr;
};

/**
 * 补零
 * @param {Number} number
 */
const coverZero = number => {
    return number.toString().length === 2 ? number.toString() : `0${number.toString()}`;
};

/**
 * 格式化视频时长
 * @param {Number} sec
 */
const formatVideoDuration = sec => {
    let seconds = sec;

    if (isNaN(sec)) {
        return '00:00';
    }

    if (typeof sec === 'string') {
        seconds = Number(sec);
    }

    let minutes = Math.floor(seconds / 60);
    const sec_left = seconds % 60;
    let hours = 0;

    if (minutes > 60) {
        hours = Math.floor(minutes / 60);
        minutes %= 60;
    }

    return hours > 0
        ? `${hours.toString()}:${coverZero(minutes)}:${coverZero(sec_left)}`
        : `${coverZero(minutes)}:${coverZero(sec_left)}`;
};

/**
 * 处理视频播放进度
 * @param {Number} current 当前时间
 * @param {Number} total 总时间
 * @returns {String} 百分比
 */
const handleVideoProgress = (current, total) => {
    const percent = (current * 100) / total;

    return `${percent.toString()}%`;
};

/**
 * 判断是否为iphone
 * @returns {Boolean} iphone是返回true，不是iphone时返回false
 */
const isIphone = () => {
    return navigator.userAgent.toLowerCase().indexOf('iphone') >= 0;
};

/**
 * 滑动当前时间处理函数
 * @param {number} startSwipTime 开始时的当前时间
 * @param {number} begin 开始坐标
 * @param {number} current 当前坐标
 * @param {number} timeBarWidth timeBar总宽度
 * @param {number} totalDuration 总时长
 */
const calTime = (startSwipTime, begin, current, timeBarWidth, totalDuration) => {
    const swipTime = startSwipTime + Math.floor(((current - begin) / timeBarWidth) * totalDuration);

    if (swipTime > totalDuration) {
        return Math.floor(totalDuration);
    }

    if (swipTime < 0) {
        return 0;
    }

    return Math.floor(swipTime);
};

/**
 * 合并对象，后者覆盖前者
 */
const extend = (...args) => {
    const result = {};

    for (let i = 0, iLen = args.length; i < iLen; i++) {
        const o = args[i];

        for (const key in o) {
            result[key] = o[key];
        }
    }

    return result;
};

/**
 * 获取屏幕长宽
 */
const getResolution = () => {
    return {
        screenW: screen.width,
        screenH: screen.height,
    };
};

/**
 * 获取浏览器型号
 */
const getBrowser = () => {
    const browser = bowser.getParser(window.navigator.userAgent);
    const browserInfo = browser.getBrowser();

    return {
        type: browserInfo.name || '',
        version: browserInfo.version || '',
    };
};

/**
 * 获取设备终端名
 */
const getOs = () => {
    const bowserInfo = bowser.getParser(window.navigator.userAgent);

    let bowserOS = {
        name: 'unknown',
        version: '',
    };

    if (bowserInfo.getOS) {
        bowserOS = bowserInfo.getOS();
    }

    return `${bowserOS.name || ''}_${bowserOS.version || ''}` || '';
};

/**
 * 判断当前浏览器是否可播放
 */
const canPlay = type => {
    const TEST_VID = document.createElement('video');

    return TEST_VID.canPlayType(type);
};

/**
 * 创建jsonp callback回调函数名
 * @param {string} name name
 */
const creatCallBackName = name => {
    let order = 0;
    let callBackName = `${name}_${order}`;

    while (window[callBackName]) {
        order++;
        callBackName = `${name}_${order}`;
    }

    return callBackName;
};

export {
    toFirstCharUpCase,
    setAttribute,
    loadVideo,
    resetVideo,
    mergeObj,
    playerGetCookie,
    playerSetCookie,
    createUid,
    formatVideoDuration,
    handleVideoProgress,
    isIphone,
    calTime,
    extend,
    getResolution,
    getBrowser,
    getOs,
    canPlay,
    creatCallBackName,
};
